import React, { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { RefreshCw, CheckCircle, XCircle, AlertCircle, Shield } from 'lucide-react'
import { api } from '../lib/api'
import { fmtPrice, timeAgo } from '../lib/utils'

// ── Timebox proximity bar ──────────────────────────────────────────────────
// 2026-09-09: replaces the old momentum-only StopProximityBar (Phase A/B
// trailing stop). Tier 2 has no trailing stop -- its two real exit paths are
// SIGNAL (oscillator crosses back past entry_high, price above entry) and
// TIMEBOX (forced exit after exit_timebox minutes held). This bar tracks
// minutes remaining until TIMEBOX forces the exit; negative means overdue --
// should exit on the very next 1-min cycle. null means this ticker's fit
// chose no timebox at all (a real, valid per-ticker setting), not missing data.
function TimeboxProximityBar({ minutes }) {
  if (minutes == null) return <span className="text-slate-700" style={{ fontSize: '0.65rem' }}>no timebox</span>
  const clamped = Math.max(-15, Math.min(minutes, 120))
  const pct100  = ((clamped + 15) / 135) * 100
  const overdue = minutes < 0
  const danger  = minutes >= 0 && minutes < 5
  const warn    = minutes >= 5 && minutes < 15
  const color   = overdue || danger ? '#f43f5e' : warn ? '#f59e0b' : '#10b981'
  return (
    <div className="flex items-center gap-2 min-w-[80px]">
      <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: '#1e293b' }}>
        <div className="h-full rounded-full transition-all" style={{ width: `${pct100}%`, background: color }} />
      </div>
      <span className="tabular-nums font-bold flex-shrink-0"
        style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem', color, minWidth: '3.2rem', textAlign: 'right' }}>
        {overdue ? 'OVERDUE' : `${minutes.toFixed(0)}m`}
      </span>
    </div>
  )
}

// ── Held duration ─────────────────────────────────────────────────────────
function fmtHeld(minutes) {
  if (minutes == null) return '—'
  if (minutes < 60)   return `${Math.round(minutes)}m`
  const h = Math.floor(minutes / 60)
  const m = Math.round(minutes % 60)
  return m > 0 ? `${h}h ${m}m` : `${h}h`
}

function SideBadge({ side }) {
  const buy = side === 'BUY'
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-bold"
      style={{
        fontSize: '0.62rem', letterSpacing: '0.08em', fontFamily: 'JetBrains Mono, monospace',
        background: buy ? 'rgba(59,130,246,0.12)' : 'rgba(100,116,139,0.12)',
        color:      buy ? '#60a5fa' : '#94a3b8',
        border:     buy ? '1px solid rgba(59,130,246,0.25)' : '1px solid rgba(100,116,139,0.2)',
      }}
    >
      {buy ? <CheckCircle className="h-2.5 w-2.5" /> : <XCircle className="h-2.5 w-2.5" />}
      {side}
    </span>
  )
}

const darkSelect = {
  background: '#111827',
  border: '1px solid #374151',
  borderRadius: '0.5rem',
  color: '#94a3b8',
  padding: '0.375rem 0.75rem',
  fontSize: '0.75rem',
  fontFamily: 'JetBrains Mono, monospace',
  letterSpacing: '0.04em',
  outline: 'none',
  cursor: 'pointer',
}

export default function Portfolio() {
  const [activity,   setActivity]   = useState([])
  const [activityStats, setActivityStats] = useState({ wins: 0, losses: 0, win_rate: 0 })
  const [positions,  setPositions]  = useState([])
  const [atRiskCount, setAtRiskCount] = useState(0)
  const [posLoading, setPosLoading] = useState(true)
  const [loading,    setLoading]    = useState(true)
  const [error,      setError]      = useState(null)
  const [days,       setDays]       = useState(7)

  // Real Tier 2 open positions -- entry/current price, unrealized P&L, and
  // the two proximities that actually govern an exit (time-to-TIMEBOX,
  // distance-to-SIGNAL). See /api/tier2/positions in app.py.
  const fetchPositions = useCallback(async () => {
    setPosLoading(true)
    try {
      const res = await api.tier2Positions()
      if (res.success) {
        setPositions(res.positions || [])
        setAtRiskCount(res.at_risk_count || 0)
      }
    } catch { /* positions are optional */ }
    finally { setPosLoading(false) }
  }, [])

  useEffect(() => { fetchPositions() }, [fetchPositions])

  // Real Tier 2 activity -- filled entries + exits, unified and time-sorted.
  // See /api/tier2/activity in app.py for why this replaces the old
  // momentum-only "Decision Engine Log" (logic_gate_decisions) instead of
  // reusing its approve/reject shape -- Tier 2 doesn't have an equivalent
  // structured rejection log.
  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.tier2Activity(days, 50)
      if (res.success) {
        setActivity(res.activity || [])
        setActivityStats({ wins: res.wins || 0, losses: res.losses || 0, win_rate: res.win_rate || 0 })
        setError(null)
      } else {
        setError(res.error || 'Failed to load activity')
      }
    } catch (e) {
      setError('Failed to load portfolio data.')
    } finally {
      setLoading(false)
    }
  }, [days])

  useEffect(() => { fetchAll() }, [fetchAll])

  const buys  = activity.filter(a => a.side === 'BUY')
  const sells = activity.filter(a => a.side === 'SELL')

  return (
    <div className="space-y-5">

      {/* Open Positions — real Tier 2, time-to-TIMEBOX / distance-to-SIGNAL */}
      <div className="rounded-xl border border-gray-800 overflow-hidden" style={{ background: '#111827' }}>
        <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid #1f2937' }}>
          <div className="flex items-center gap-3">
            <Shield className="h-4 w-4 text-slate-600" />
            <div>
              <h3 className="section-header" style={{ marginBottom: 0 }}>Open Positions</h3>
              <p className="text-slate-700" style={{ fontSize: '0.62rem', fontFamily: 'JetBrains Mono, monospace' }}>
                Tier 2 (live) · {positions.length} positions · exits via SIGNAL or TIMEBOX · {atRiskCount > 0 ? `${atRiskCount} within 5min of TIMEBOX` : 'none at risk'}
              </p>
            </div>
          </div>
          <button onClick={fetchPositions}
            className="flex items-center gap-1.5 border border-gray-700 rounded-lg px-3 py-1.5 text-slate-500 hover:text-slate-300 hover:bg-gray-800 transition-colors"
            style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem' }}>
            <RefreshCw className={`h-3 w-3 ${posLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {posLoading ? (
          <div className="p-8 text-center text-slate-700">
            <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" />
            <p style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem' }}>LOADING POSITIONS…</p>
          </div>
        ) : positions.length === 0 ? (
          <div className="p-8 text-center text-slate-700">
            <p style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem' }}>NO OPEN POSITIONS</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead style={{ background: '#0f172a' }}>
                <tr>
                  {[
                    { label: 'Symbol',           align: 'left'  },
                    { label: 'Held',             align: 'left'  },
                    { label: 'Entry',            align: 'right' },
                    { label: 'Current',          align: 'right' },
                    { label: 'P&L %',            align: 'right' },
                    { label: 'Signal Distance',  align: 'right' },
                    { label: 'Time to Timebox',  align: 'left'  },
                  ].map(({ label, align }) => (
                    <th key={label} className={`px-4 py-3 text-${align}`}
                      style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.6rem',
                               letterSpacing: '0.1em', color: '#4b5563', fontWeight: 600, textTransform: 'uppercase' }}>
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {positions.map((p, i) => {
                  const unrlzColor = p.pnl_pct == null ? '#64748b' : p.pnl_pct >= 0 ? '#10b981' : '#f43f5e'
                  const sigColor = p.signal_distance == null ? '#64748b'
                    : p.signal_distance <= 0 ? '#10b981' : p.signal_distance <= 5 ? '#f59e0b' : '#64748b'
                  return (
                    <tr key={p.symbol} style={{ background: i % 2 === 0 ? '#0f172a' : '#111827' }}
                      className="hover:bg-gray-800 transition-colors">
                      <td className="px-4 py-2.5 font-semibold text-slate-200" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        {p.symbol}
                        {!p.tracked && (
                          <span title="Real Alpaca position with no tracking row -- next reconcile will heal it"
                            className="ml-1.5 text-amber-500" style={{ fontSize: '0.6rem' }}>⚠</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-slate-500" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem' }}>
                        {fmtHeld(p.minutes_held)}
                      </td>
                      <td className="px-4 py-2.5 text-right text-slate-400 tabular-nums" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.75rem' }}>
                        {fmtPrice(p.entry_price)}
                      </td>
                      <td className="px-4 py-2.5 text-right text-slate-300 tabular-nums" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.75rem' }}>
                        {p.current_price ? fmtPrice(p.current_price) : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-medium"
                        style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.75rem', color: unrlzColor }}>
                        {p.pnl_pct != null ? `${p.pnl_pct >= 0 ? '+' : ''}${p.pnl_pct.toFixed(2)}%` : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums"
                        style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.72rem', color: sigColor }}
                        title={p.oscillator_value != null ? `${p.entry_indicator?.toUpperCase()} now ${p.oscillator_value} · exits above ${p.entry_high}` : ''}>
                        {p.signal_distance != null ? (p.signal_distance <= 0 ? 'past threshold' : p.signal_distance.toFixed(1)) : '—'}
                      </td>
                      <td className="px-4 py-2.5">
                        <TimeboxProximityBar minutes={p.minutes_to_timebox} />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Period selector + refresh */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-slate-600" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem', letterSpacing: '0.08em' }}>
            PERIOD:
          </span>
          {[1, 3, 7, 14].map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
              style={{
                fontFamily: 'JetBrains Mono, monospace',
                letterSpacing: '0.06em',
                background: days === d ? '#1d4ed8'  : '#111827',
                color:      days === d ? '#bfdbfe'  : '#64748b',
                border:     days === d ? '1px solid rgba(59,130,246,0.4)' : '1px solid #374151',
                boxShadow:  days === d ? '0 0 8px rgba(59,130,246,0.3)' : 'none',
              }}
            >
              {d}D
            </button>
          ))}
        </div>
        <button
          onClick={fetchAll}
          className="flex items-center gap-1.5 border border-gray-700 rounded-lg px-3 py-1.5 text-slate-500 hover:text-slate-300 hover:bg-gray-800 transition-colors"
          style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem', letterSpacing: '0.08em' }}
        >
          <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
          REFRESH
        </button>
      </div>

      {error && (
        <div
          className="rounded-lg px-4 py-3 text-sm"
          style={{ background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.2)', color: '#fbbf24' }}
        >
          {error}
        </div>
      )}

      {/* KPI row — real Tier 2 outcomes over the selected period */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Trades', value: sells.length,                    color: '#94a3b8', border: '#475569' },
          { label: 'Wins',         value: activityStats.wins,               color: '#10b981', border: '#10b981' },
          { label: 'Losses',       value: activityStats.losses,             color: '#f43f5e', border: '#f43f5e' },
          { label: 'Win Rate',     value: `${activityStats.win_rate}%`,     color: '#3b82f6', border: '#3b82f6' },
        ].map(({ label, value, color, border }) => (
          <div
            key={label}
            className="rounded-xl p-4 text-center"
            style={{
              background: '#111827',
              border: '1px solid #1f2937',
              borderTop: `1.5px solid ${border}`,
              boxShadow: `0 -1px 8px ${border}33`,
            }}
          >
            <p className="text-slate-600 uppercase" style={{ fontSize: '0.62rem', letterSpacing: '0.12em', fontWeight: 600 }}>
              {label}
            </p>
            <p
              className="font-bold mt-1.5 tabular-nums"
              style={{ color, fontFamily: 'JetBrains Mono, monospace', fontSize: '1.5rem' }}
            >
              {loading ? '—' : value}
            </p>
          </div>
        ))}
      </div>

      {/* Recent Activity — real Tier 2 entries + exits, time-sorted */}
      <div className="rounded-xl border border-gray-800 overflow-hidden" style={{ background: '#111827' }}>
        <div className="px-5 py-4 flex items-start justify-between" style={{ borderBottom: '1px solid #1f2937' }}>
          <div>
            <h3 className="section-header" style={{ marginBottom: '0.2rem' }}>Recent Activity</h3>
            <p className="text-slate-700" style={{ fontSize: '0.65rem', fontFamily: 'JetBrains Mono, monospace' }}>
              Tier 2 real fills — {buys.length} entries, {sells.length} exits in range
            </p>
          </div>
          {activity.length > 0 && (
            <span className="text-slate-700" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem' }}>
              {activity.length} rows
            </span>
          )}
        </div>

        {loading ? (
          <div className="p-10 text-center text-slate-700">
            <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" />
            <p style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem' }}>LOADING ACTIVITY…</p>
          </div>
        ) : activity.length === 0 ? (
          <div className="p-10 text-center">
            <AlertCircle className="h-9 w-9 mx-auto mb-2 text-slate-800" />
            <p className="text-slate-600 text-sm">No activity in this period</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead style={{ background: '#0f172a' }}>
                <tr>
                  {['Time', 'Symbol', 'Side', 'Price', 'P&L %', 'Reason'].map((h, i) => (
                    <th
                      key={h}
                      className={`px-4 py-3 ${i === 3 || i === 4 ? 'text-right' : 'text-left'}`}
                      style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.6rem', letterSpacing: '0.1em', color: '#4b5563', fontWeight: 600, textTransform: 'uppercase' }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {activity.map((a, i) => {
                  const pnlColor = a.pnl_pct == null ? '#64748b' : a.pnl_pct >= 0 ? '#10b981' : '#f43f5e'
                  return (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#0f172a' : '#111827' }} className="hover:bg-gray-800 transition-colors">
                      <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem' }}>
                        {timeAgo(a.time)}
                      </td>
                      <td className="px-4 py-2.5 font-semibold text-slate-200" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        {a.symbol}
                      </td>
                      <td className="px-4 py-2.5"><SideBadge side={a.side} /></td>
                      <td className="px-4 py-2.5 text-right text-slate-400 tabular-nums" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.75rem' }}>
                        {a.price != null ? fmtPrice(a.price) : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-medium" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.75rem', color: pnlColor }}>
                        {a.pnl_pct != null ? `${a.pnl_pct >= 0 ? '+' : ''}${a.pnl_pct.toFixed(2)}%` : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-slate-600 max-w-xs truncate" style={{ fontSize: '0.7rem' }}>
                        {a.reason || '—'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Risk configuration lived here as a hardcoded card that drifted out of
          sync with the live system. Settings → System Configuration is the
          one live source of truth for these values now — link out instead
          of duplicating it. */}
      <Link to="/settings"
        className="flex items-center justify-between rounded-xl border border-gray-800 px-5 py-3.5 hover:border-gray-700 transition-colors"
        style={{ background: '#111827' }}>
        <span className="flex items-center gap-2 text-slate-400" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.72rem' }}>
          <Shield className="h-3.5 w-3.5 text-slate-600" />
          Live risk & execution configuration
        </span>
        <span className="text-cyan-400" style={{ fontSize: '0.7rem', fontFamily: 'JetBrains Mono, monospace' }}>
          View in Settings →
        </span>
      </Link>
    </div>
  )
}
